<template>
  <div class="border-container">
    <div id="preview-border" teleport></div>
    <perfect-scrollbar class="scrollBody">
      <div class="body">
        <div class="wrap-editor-border">
          <Pack
            v-for="(border, index) in currentPresetOptions"
            :key="index"
            :ref="`border-pack-${index}`"
            :item="border"
            :index-item="index"
            :selected-pack="selectedPack"
            :is-ready-to-save="isReadyToSave"
            :invalid-pack="invalidPack"
            :type-pack="'border'"
            @validateItemName="validateItemName"
            @toggleAction="toggleAction"
            @onChangePack="selectedPack = $event"
            @onDelete="deleteItem"
            @onDuplicate="duplicateItem">
            <template #settingSlot>
              <BorderItem
                :key="index"
                :is-ready-to-save="isReadyToSave"
                :toggle-event="toggleEvent"
                :border="border"
                :index="index"
                :position-top-preview="positionTopPreview" />
            </template>
          </Pack>
        </div>
        <div v-if="newElementValid" class="wrap-btn-border-action">
          <a class="btn btn-corner d-flex" @click="addNewBorder">
            <i class="icon icon-add" />
            <span>Add New Border</span>
          </a>
        </div>
      </div>
      <GuideCustomizeStyle
        v-if="!obGlobalStyle.tooltipBorderStyle"
        title="Setup Border"
        guide="tooltipBorderStyle"
        link="https://help.gempages.net/articles/customize-global-style#table-content-2"
        content="You can choose to use border style for elements you need. You can find the option in tab Design of element." />
    </perfect-scrollbar>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import BorderItem from './BorderItem.vue';
import { mapMutations, mapState, mapGetters, mapActions } from 'vuex';
import { presetOptionTypes } from '../../constants/styleConstant';
import { mutations } from '@modules/editor-sidebar';
import type { PresetOption, PresetOptionType } from '@/types/global-style';
import Pack from './/Pack.vue';
import { cloneDeep } from '@/utils/common';
import GuideCustomizeStyle from '../guide/GuideCustomizeStyle.vue';
import HELPER from '@/app/utils/HELPER';

interface DataInterface {
  presetBorderType: string;
  selectedPack: number;
  toggleEvent: any;
  isReadyToSave: boolean;
  invalidPack: any;
  positionTopPreview: any;
}

export default defineComponent({
  name: 'Border',
  components: {
    GuideCustomizeStyle,
    BorderItem,
    Pack,
  },
  data: (): DataInterface => ({
    presetBorderType: 'border',
    selectedPack: -1,
    isReadyToSave: true,
    toggleEvent: {},
    invalidPack: -1,
    positionTopPreview: 0,
  }),
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
      styleIdTargetGlobalStyle: (state) => (state as any).styleIdTargetGlobalStyle,
    }),
    ...mapGetters('sidebar', {
      currentPresetOptions: 'getCurrentPresetOptions',
      currentPresetColor: 'getCurrentPresetColor',
    }),
    ...mapState('app', {
      shopMeta: 'shopMeta',
    }),
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
    elementNames() {
      return this.currentPresetOptions
        .filter((element: { destroy: any }) => !element.destroy)
        .map((element: { name: any }) => element.name);
    },
    newElementValid() {
      return this.elementNames.length < 10;
    },
  } as any,
  watch: {
    styleIdTargetGlobalStyle(val) {
      if (val) {
        const indexItem = this.currentPresetOptions.findIndex((item: any) => item.id == val);
        this.selectedPack = indexItem;
      }
    },
    isReadyToSave(newVal) {
      this.$emit('isReadyToSave', newVal);
    },
    selectedPack(val) {
      const self = this;
      if (val != -1) {
        self.$nextTick(() => {
          const top = self.$refs[`border-pack-${val}`][0].$el.getBoundingClientRect().top - 100;
          self.positionTopPreview = top + Math.random();
        });
      } else {
        self.positionTopPreview = 0;
      }
    },
  },
  mounted() {
    this.setPresetTypeId(presetOptionTypes.border);
    this.makeCurrentPresetOptions();
    this.setStyleIdTargetGS(parseInt(this.styleIdTargetGlobalStyle));
    const indexItem = this.currentPresetOptions.findIndex((item: any) => item.id == this.styleIdTargetGlobalStyle);
    this.selectedPack = indexItem;
  },
  methods: {
    ...mapMutations('sidebar', {
      setPresetTypeId: mutations.SET_PRESET_TYPE_ID,
      setCurrentPresetOptions: mutations.SET_CURRENT_PRESET_OPTIONS,
      setStyleIdTargetGS: mutations.SET_STYLE_ID_TARGET_GLOBAL_STYLE,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    makeCurrentPresetOptions(): void {
      this.setCurrentPresetOptions(cloneDeep(this.currentPreset.presetOptions.borders));
    },
    validateItemName(data: any): void {
      this.isReadyToSave = data.isAvailable;
      this.invalidPack = data.invalidPack;
    },
    toggleAction(e: any) {
      this.toggleEvent = e;
    },
    deleteItem(index: number) {
      if (!this.isReadyToSave) return;

      if (this.currentPresetOptions[index].id) {
        this.currentPresetOptions[index].destroy = true;
        this.currentPresetOptions[index].name = undefined;
      } else {
        this.currentPresetOptions.splice(index, 1);
      }
    },
    duplicateItem(index: number) {
      if (!this.isReadyToSave) return;
      if (!this.newElementValid) return;

      const iDuplicate = this.currentPresetOptions[index];

      let i = 1;

      while (this.elementNames.includes(this.generateDuplicateName(iDuplicate.name, i))) i++;

      const duplicateItem: PresetOption = {
        id: `gpid_${HELPER.MakeId()}`,
        isDefault: false,
        key: '',
        name: this.generateDuplicateName(iDuplicate.name, i),
        type: presetOptionTypes.border as PresetOptionType,
        destroy: false,
        value: cloneDeep(iDuplicate.value),
      };

      this.currentPresetOptions.push(duplicateItem);
    },
    generateDuplicateName(name: string, index: number): string {
      return `${name} ${index > 0 ? index : ''}`.trim();
    },
    generateCreateNewName(index: number): string {
      return `Border ${index > 0 ? index : ''}`.trim();
    },
    addNewBorder() {
      if (!this.isReadyToSave) return;
      if (!this.newElementValid) return;
      let i = 1;

      while (this.elementNames.includes(this.generateCreateNewName(i))) i++;

      const newBorder: PresetOption = {
        id: `gpid_${HELPER.MakeId()}`,
        isDefault: false,
        key: '',
        name: this.generateCreateNewName(i),
        type: presetOptionTypes.border as PresetOptionType,
        value: {
          color: this.currentPresetColor.value.neutral.color3,
          dash: '5px',
          gap: '5px',
          dashCap: 'none',
          thickness: '5px',
          style: 'solid',
        },
      };

      this.currentPresetOptions.push(newBorder);
    },
  } as any,
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.scrollBody {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 4px;

  .body {
    flex: 1;
    margin-bottom: 8px;
  }
}

.border-container {
  height: calc(100% - 125px);
}

.wrap-editor-border {
  -moz-border-radius: $radius;
  -webkit-border-radius: $radius;
  background: $black;
  color: $highlight2;
  font-size: 12px;
  padding: 12px;
}

.wrap-list-corner-style {
  display: flex;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 15px;
}

.wrap-btn-border-action {
  .btn-corner {
    margin-right: 12px;
    margin-left: 12px;
    background: unset;
    border: 1px dashed $neutral_5;
    border-radius: $radius;
    -moz-border-radius: $radius;
    -webkit-border-radius: $radius;
    position: relative;
    font-size: 12px;

    .icon-add {
      margin-right: 11px;
      width: 11px;
      height: 11px;
    }
  }
}
</style>
